import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { setFocusOnExtent } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { activeSource } from 'components/map/Layers';

import { boundingExtent, containsExtent } from 'ol/extent';
import { Point } from 'ol/geom';

import { useAppDispatch, useAppSelector } from './defaultStoreHooks';

export const useInitialFocusOnExtent = (callback: () => void) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const visibleMapExtent = useAppSelector((state: RootState) => state.map.visibleMapExtent);
  const locationKeyRef = useRef<string>();

  useEffect(() => {
    const handleAddFeatureEvent = () => {
      activeSource.un('addfeature', handleAddFeatureEvent);

      const features = activeSource.getFeatures();
      const action = (location.state as any)?.action;
      const points = features.filter((f) => f.getGeometry()?.getType() === 'Point');

      if (!features.length || !visibleMapExtent || locationKeyRef.current === location.key) return;
      locationKeyRef.current = location.key;

      const coords = points.map((p) => (p.getGeometry() as Point).getCoordinates());
      const extentFromPoints = boundingExtent(coords);
      const visibleMapExtentContainsExtentFromPoints = containsExtent(visibleMapExtent, extentFromPoints);

      if (visibleMapExtentContainsExtentFromPoints) return;

      // Har vi flera punkter och extenten för dessa inte är synlig behöver vi köra fit istället för animate, då animate kan putta ut punkter utanför visibleMapExtent.
      const fitIfMultipleGeometryPoints = points.length > 1;

      if (action === 'fit' || fitIfMultipleGeometryPoints) {
        dispatch(
          setFocusOnExtent({
            type: 'fit',
            extent: activeSource.getExtent(),
            zoomLevel: 12.3,
            duration: fitIfMultipleGeometryPoints ? 250 : 0
          })
        );

        window.history.replaceState(action, '');
      } else if (points.length === 1) {
        dispatch(
          setFocusOnExtent({
            type: 'animate',
            extent: activeSource.getExtent()
          })
        );
      }
    };

    activeSource.on('addfeature', handleAddFeatureEvent);
  }, [dispatch, location.key, location.state, visibleMapExtent]);

  useEffect(() => {
    callback();
  }, [callback]);
};
